import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import type { Sequence } from "@/lib/types";
import {
	Zap,
	Play,
	Pause,
	FileEdit,
	Mail,
	Linkedin,
	Smartphone,
	Users,
	ChevronRight,
} from "lucide-react";

interface SequencesProps {
	sequences: Sequence[];
	onSelect: (id: string) => void;
}

const statusConfig: Record<string, { label: string; className: string }> = {
	active: { label: "Active", className: "bg-neon-green/15 text-neon-green border-neon-green/30" },
	paused: { label: "Paused", className: "bg-neon-purple/15 text-neon-purple border-neon-purple/30" },
	draft: { label: "Draft", className: "bg-muted text-muted-foreground border-border" },
};

const channelIcons: Record<string, React.ReactNode> = {
	email: <Mail className="size-3.5" />,
	linkedin: <Linkedin className="size-3.5" />,
	sms: <Smartphone className="size-3.5" />,
};

const channelColors: Record<string, string> = {
	email: "text-neon-cyan",
	linkedin: "text-neon-blue",
	sms: "text-neon-green",
};

export function Sequences({ sequences, onSelect }: SequencesProps) {
	return (
		<div className="p-6 space-y-6">
			{/* Header */}
			<div className="flex items-center justify-between">
				<div>
					<h1 className="text-2xl font-bold">Sequences</h1>
					<p className="text-muted-foreground text-sm mt-1">
						Manage your multichannel engagement sequences
					</p>
				</div>
				<Button className="bg-neon-cyan text-black font-semibold hover:bg-neon-cyan/90">
					<Zap className="size-4 mr-2" />
					New Sequence
				</Button>
			</div>

			{/* Sequence Cards */}
			<div className="grid gap-4">
				{sequences.map((seq) => {
					const status = statusConfig[seq.status];
					const uniqueChannels = [...new Set(seq.steps.map((s) => s.channel))];
					const replyRate =
						seq.metrics.sent > 0
							? ((seq.metrics.replied / seq.metrics.sent) * 100).toFixed(1)
							: "0";

					return (
						<Card
							key={seq.id}
							className="border-border/50 hover:border-neon-cyan/30 transition-all cursor-pointer group"
							onClick={() => onSelect(seq.id)}
						>
							<CardContent className="py-4 px-5">
								<div className="flex items-center gap-4">
									{/* Status Icon */}
									<div className="size-10 rounded-xl bg-secondary flex items-center justify-center shrink-0">
										{seq.status === "active" ? (
											<Play className="size-4 text-neon-green" />
										) : seq.status === "paused" ? (
											<Pause className="size-4 text-neon-purple" />
										) : (
											<FileEdit className="size-4 text-muted-foreground" />
										)}
									</div>

									{/* Info */}
									<div className="flex-1 min-w-0">
										<div className="flex items-center gap-2 mb-1">
											<h3 className="font-semibold truncate">{seq.name}</h3>
											<Badge variant="outline" className={status.className}>
												{status.label}
											</Badge>
										</div>
										<p className="text-sm text-muted-foreground truncate">
											{seq.description}
										</p>
									</div>

									{/* Channels */}
									<div className="flex items-center gap-1.5 shrink-0">
										{uniqueChannels.map((ch) => (
											<div
												key={ch}
												className={`size-7 rounded-md bg-secondary flex items-center justify-center ${channelColors[ch]}`}
											>
												{channelIcons[ch]}
											</div>
										))}
									</div>

									{/* Steps */}
									<div className="text-center shrink-0 w-16">
										<p className="text-lg font-bold">{seq.steps.length}</p>
										<p className="text-xs text-muted-foreground">steps</p>
									</div>

									{/* Contacts */}
									<div className="flex items-center gap-1.5 text-muted-foreground shrink-0 w-20">
										<Users className="size-3.5" />
										<span className="text-sm">{seq.contactCount}</span>
									</div>

									{/* Metrics */}
									<div className="text-center shrink-0 w-24">
										<p className="text-lg font-bold text-neon-green">{replyRate}%</p>
										<p className="text-xs text-muted-foreground">reply rate</p>
									</div>

									{/* Arrow */}
									<ChevronRight className="size-4 text-muted-foreground group-hover:text-neon-cyan transition-colors shrink-0" />
								</div>
							</CardContent>
						</Card>
					);
				})}
			</div>
		</div>
	);
}
